import { z } from 'zod';

// Legal document keys
export const LegalDocumentKeyEnum = {
  TERMS: 'terms',
  PRIVACY: 'privacy',
  BETA: 'beta',
} as const;

export type LegalDocumentKey = typeof LegalDocumentKeyEnum[keyof typeof LegalDocumentKeyEnum];

// Current legal document versions and URLs
export const LEGAL_DOCUMENTS = {
  [LegalDocumentKeyEnum.TERMS]: {
    key: LegalDocumentKeyEnum.TERMS,
    name: 'Terms of Service',
    url: 'https://scrambl.com/terms',
    version: '2025-01-01',
  },
  [LegalDocumentKeyEnum.PRIVACY]: {
    key: LegalDocumentKeyEnum.PRIVACY,
    name: 'Privacy Policy',
    url: 'https://scrambl.com/privacy',
    version: '2025-01-01',
  },
  [LegalDocumentKeyEnum.BETA]: {
    key: LegalDocumentKeyEnum.BETA,
    name: 'Beta Services Addendum',
    url: 'https://scrambl.com/beta',
    version: '2025-01-01',
  },
} as const;

// Schema for legal agreement acceptance during org creation
export const LegalAgreementAcceptanceInputSchema = z.object({
  documentKey: z.enum([LegalDocumentKeyEnum.TERMS, LegalDocumentKeyEnum.PRIVACY, LegalDocumentKeyEnum.BETA]),
  documentVersion: z.string(),
  documentUrl: z.string().url(),
  acceptedAt: z.string().datetime().optional(), // ISO string, will default to now
});

export type LegalAgreementAcceptanceInput = z.infer<typeof LegalAgreementAcceptanceInputSchema>;

// Schema for terms acceptance during organization creation
export const TermsAcceptanceSchema = z.object({
  termsAccepted: z.boolean().refine((val) => val === true, {
    message: 'You must accept the Terms of Service to continue',
  }),
  privacyAccepted: z.boolean().refine((val) => val === true, {
    message: 'You must accept the Privacy Policy to continue',
  }),
  betaAccepted: z.boolean().refine((val) => val === true, {
    message: 'You must accept the Beta Services Addendum to continue',
  }),
  acceptedAt: z.string().datetime().optional(),
  ipAddress: z.string().optional(),
  userAgent: z.string().optional(),
});

export type TermsAcceptance = z.infer<typeof TermsAcceptanceSchema>;

// Schema for legal document
export const LegalDocumentSchema = z.object({
  id: z.string().uuid(),
  key: z.string(),
  name: z.string(),
  url: z.string().url(),
  version: z.string(),
  effectiveAt: z.date(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type LegalDocument = z.infer<typeof LegalDocumentSchema>;

// Schema for legal agreement acceptance record
export const LegalAgreementAcceptanceSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  userId: z.string().uuid(),
  legalDocumentId: z.string().uuid(),
  documentVersion: z.string(),
  documentUrl: z.string().url(),
  acceptedAt: z.date(),
  ipAddress: z.string().nullable().optional(),
  userAgent: z.string().nullable().optional(),
});

export type LegalAgreementAcceptance = z.infer<typeof LegalAgreementAcceptanceSchema>;

// Helper to get all required legal documents for org creation
export function getRequiredLegalDocuments() {
  return [
    LEGAL_DOCUMENTS[LegalDocumentKeyEnum.TERMS],
    LEGAL_DOCUMENTS[LegalDocumentKeyEnum.PRIVACY],
    LEGAL_DOCUMENTS[LegalDocumentKeyEnum.BETA],
  ];
}

// Helper to check if all required agreements are accepted
export function areAllAgreementsAccepted(acceptance: Partial<TermsAcceptance>): boolean {
  return (
    acceptance.termsAccepted === true &&
    acceptance.privacyAccepted === true &&
    acceptance.betaAccepted === true
  );
}
